import React from 'react';
import { Users, Plus, Calendar, TrendingUp, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { mockCooperatives, formatCurrency } from '@/utils/mockData';
import { toast } from 'sonner';

export default function CooperativesPage() {
  const handleJoinGroup = () => {
    toast.success('Successfully joined the group!');
  };

  return (
    <div className="max-w-6xl mx-auto" data-testid="cooperatives-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold font-heading text-slate-900">Cooperatives & Groups</h1>
          <p className="text-slate-600 mt-1">Join Esusu, Ajo, or create custom savings groups</p>
        </div>
        <Button data-testid="create-group-btn" className="bg-brand-blue text-white hover:bg-blue-700 rounded-full px-6 shadow-lg transition-all active:scale-95">
          <Plus className="w-5 h-5 mr-2" />
          Create Group
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Active Groups</p>
              <p className="text-2xl font-bold font-heading text-slate-900">{mockCooperatives.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Total Pool</p>
              <p className="text-2xl font-bold font-heading text-slate-900">₦1,400,000</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 text-brand-blue flex items-center justify-center">
              <Calendar className="w-6 h-6" />
            </div>
            <div>
              <p className="text-sm text-slate-600">Next Payout</p>
              <p className="text-2xl font-bold font-heading text-slate-900">Dec 27</p>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <h3 className="text-lg font-bold font-heading text-slate-900">My Groups</h3>
        {mockCooperatives.map((coop) => (
          <div key={coop.id} data-testid={`cooperative-${coop.id}`} className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:border-brand-blue/50 transition-all">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center flex-shrink-0">
                <Users className="w-8 h-8 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-xl font-bold font-heading text-slate-900">{coop.name}</h4>
                    <p className="text-sm text-slate-600 capitalize">{coop.type} • {coop.members} members</p>
                  </div>
                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 capitalize">
                    {coop.status}
                  </span>
                </div>
                
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-600 mb-1">Contribution</p>
                    <p className="font-bold text-slate-900">{formatCurrency(coop.contributionAmount)}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-600 mb-1">Frequency</p>
                    <p className="font-bold text-slate-900 capitalize">{coop.frequency}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-600 mb-1">Next Payout</p>
                    <p className="font-bold text-slate-900">{coop.nextPayout}</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-3">
                    <p className="text-xs text-slate-600 mb-1">{coop.type === 'rotating' ? 'My Turn' : 'Progress'}</p>
                    <p className="font-bold text-slate-900">
                      {coop.type === 'rotating' 
                        ? `Position ${coop.myTurn}` 
                        : `${((coop.currentAmount / coop.target) * 100).toFixed(0)}%`
                      }
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Button size="sm" data-testid={`contribute-btn-${coop.id}`} className="bg-brand-blue text-white hover:bg-blue-700 rounded-xl transition-all active:scale-95">
                    Make Contribution
                  </Button>
                  <Button size="sm" variant="outline" data-testid={`view-details-btn-${coop.id}`} className="rounded-xl">
                    View Details
                  </Button>
                  <Button size="sm" variant="ghost" data-testid={`chat-btn-${coop.id}`} className="rounded-xl">
                    Group Chat
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 bg-gradient-to-br from-purple-600 to-purple-700 rounded-2xl p-8 text-white">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div className="space-y-4">
            <h3 className="text-2xl font-bold font-heading">Start Your Own Savings Group</h3>
            <p className="text-purple-100 leading-relaxed">Invite friends, family, or colleagues to save together. Set contribution amounts, rotation schedules, and watch everyone achieve their goals.</p>
            <Button data-testid="start-group-cta-btn" className="bg-white text-purple-600 hover:bg-purple-50 rounded-xl px-6 py-3 font-semibold transition-all active:scale-95">
              Create New Group
            </Button>
          </div>
          <div className="space-y-3">
            {['Set custom rules', 'Automatic reminders', 'Transparent tracking', 'Secure payouts'].map((feature, idx) => (
              <div key={idx} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4" />
                </div>
                <span className="text-purple-50">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
